import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import get from 'lodash/get';
import { Col, Container, Row, Table } from 'react-bootstrap';
import { clientOnly } from 'utils/react';
import { allSections } from 'components/sections';
import { allWidgets } from 'components/widgets';
import classes from './Dashboard.scss';

const RandomData = ({
  siteCount,
  pageCount,
  facilityCount,
  certificateCount,
  sectionUsage,
  widgetUsage,
  facilitiesPerCompany,
}) => {
  const BarChart = require('react-chartjs-2').Bar; // eslint-disable-line global-require

  const sectionCount = sectionUsage.reduce((count, section) => count + section.count, 0);
  const widgetCount = widgetUsage.reduce((count, widget) => count + widget.count, 0);
  const companiesPerFacilityCount = facilitiesPerCompany.reduce((accumulator, { count }) => {
    let bucket = accumulator.find(bin => bin.count === count);
    if (!bucket) {
      bucket = { count, facilities: 0 };
      accumulator.push(bucket);
    }
    bucket.facilities++;

    return accumulator;
  }, []);
  companiesPerFacilityCount.sort((left, right) => left.count - right.count);

  return (
    <Container fluid className={classes.padTop}>
      <Row>
        <Col sm={12}>
          <Table>
            <tbody>
              <tr>
                <th>Pages</th>
                <td>
                  {pageCount}
                  {' '}
                  <span className="text-muted">(about {Math.round(pageCount / siteCount)} per site)</span>
                </td>
              </tr>
              <tr>
                <th>Facilities</th>
                <td>
                  {facilityCount}
                  {' '}
                  <span className="text-muted">(about {Math.round(facilityCount / siteCount)} per site)</span>
                </td>
              </tr>
              <tr>
                <th>SSL Certificates</th>
                <td>{certificateCount}</td>
              </tr>
              <tr>
                <th>Sections Used</th>
                <td>
                  {sectionCount}
                  {' '}
                  <span className="text-muted">(about {Math.round(sectionCount / pageCount)} per page)</span>
                </td>
              </tr>
              <tr>
                <th>Widgets Used</th>
                <td>
                  {widgetCount}
                  {' '}
                  <span className="text-muted">(about {Math.round(widgetCount / pageCount)} per page)</span>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
      <Row>
        <Col sm={12} lg={6}>
          <h4>Section Usage</h4>

          <BarChart
            data={{
              labels: sectionUsage.map(({ section }) => get(allSections, `${section}.details.name`, 'Unknown')),
              datasets: [{
                label: 'Sections',
                data: sectionUsage.map(({ count }) => count),
                backgroundColor: '#2ba7df',
              }],
            }}
            options={{
              scales: {
                yAxes: [{
                  ticks: {
                    suggestedMin: 0,
                  },
                }],
              },
            }}
          />
        </Col>
        <Col sm={12} lg={6}>
          <h4>Widget Usage</h4>

          <BarChart
            data={{
              labels: widgetUsage.map(({ widget }) => get(allWidgets, `${widget}.manifestInfo.name`, 'Unknown')),
              datasets: [{
                label: 'Widgets',
                data: widgetUsage.map(({ count }) => count),
                backgroundColor: '#8fc83c',
              }],
            }}
            options={{
              scales: {
                yAxes: [{
                  ticks: {
                    suggestedMin: 0,
                  },
                }],
              },
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12} lg={6}>
          <h4>Facilities per Company</h4>
          {/* TODO: Update this statistic to be "facilities listed per site" once we start using listings */}

          <BarChart
            data={{
              labels: companiesPerFacilityCount.map(({ count }) => pluralize('facility', count, true)),
              datasets: [{
                label: 'Companies',
                data: companiesPerFacilityCount.map(({ facilities }) => facilities),
                backgroundColor: '#2ba7df',
              }],
            }}
            options={{
              scales: {
                yAxes: [{
                  ticks: {
                    suggestedMin: 0,
                  },
                }],
              },
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};
RandomData.propTypes = {
  siteCount: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  facilityCount: PropTypes.number.isRequired,
  certificateCount: PropTypes.number.isRequired,
  sectionUsage: PropTypes.arrayOf(PropTypes.shape({
    section: PropTypes.string,
    count: PropTypes.number.isRequired,
  })).isRequired,
  widgetUsage: PropTypes.arrayOf(PropTypes.shape({
    widget: PropTypes.string,
    count: PropTypes.number.isRequired,
  })).isRequired,
  facilitiesPerCompany: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string,
    count: PropTypes.number.isRequired,
  })).isRequired,
};

export default clientOnly()(RandomData);
